import { useEffect } from "react";
import { useI18n } from "../context/I18nContext";
import { LANGUAGE_EN, LANGUAGE_ZH, translateUiText } from "./translations";

const SKIP_SELECTOR = [
  "script",
  "style",
  "code",
  "pre",
  "textarea",
  "[data-no-translate]",
  "[data-no-translate] *",
].join(",");

const TRANSLATABLE_ATTRS = ["placeholder", "title", "aria-label"];

const shouldSkipElement = (element) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;
  return element.matches(SKIP_SELECTOR) || !!element.closest("[data-no-translate]");
};

const translateTextNode = (node, language) => {
  const parent = node.parentElement;
  if (!parent || shouldSkipElement(parent)) return;
  const next = translateUiText(node.nodeValue, language);
  if (next !== node.nodeValue) node.nodeValue = next;
};

const translateAttributes = (element, language) => {
  if (!element || shouldSkipElement(element)) return;
  TRANSLATABLE_ATTRS.forEach((attr) => {
    if (!element.hasAttribute(attr)) return;
    const value = element.getAttribute(attr);
    const next = translateUiText(value, language);
    if (next !== value) element.setAttribute(attr, next);
  });
};

const walkAndTranslate = (root, language) => {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) {
    translateTextNode(root, language);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE && shouldSkipElement(root)) return;

  if (root.nodeType === Node.ELEMENT_NODE) {
    translateAttributes(root, language);
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      translateTextNode(node, language);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      translateAttributes(node, language);
      if (shouldSkipElement(node)) {
        walker.currentNode = node;
      }
    }
    node = walker.nextNode();
  }
};

const getTranslationLanguage = (language) => (language === LANGUAGE_EN ? LANGUAGE_EN : LANGUAGE_ZH);

const I18nDomTranslator = () => {
  const { language } = useI18n();

  useEffect(() => {
    const targetLanguage = getTranslationLanguage(language);
    const translate = () => walkAndTranslate(document.body, targetLanguage);
    const frame = window.requestAnimationFrame(translate);

    const observer = new MutationObserver((mutations) => {
      window.requestAnimationFrame(() => {
        mutations.forEach((mutation) => {
          if (mutation.type === "characterData") {
            translateTextNode(mutation.target, targetLanguage);
            return;
          }
          mutation.addedNodes.forEach((node) => walkAndTranslate(node, targetLanguage));
          if (mutation.type === "attributes") {
            translateAttributes(mutation.target, targetLanguage);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRS,
    });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [language]);

  return null;
};

export default I18nDomTranslator;

